// SPDX-FileCopyrightText: 2026 Ben Richardson
// SPDX-License-Identifier: AGPL-3.0-or-later
//
// Assume the reader knows nothing about the honours system. Every piece of
// jargon on the site is a glossary term with a click-to-show definition.

import { escapeHtml } from './format';

export const GLOSSARY: Record<string, { term: string; def: string }> = {
  oam: {
    term: 'OAM — Medal of the Order of Australia',
    def: 'The most widely awarded level of the Order of Australia, for service worthy of particular recognition. About two-thirds of all Order of Australia awards are OAMs.',
  },
  am: {
    term: 'AM — Member of the Order of Australia',
    def: 'The third-highest level, for service in a particular locality, field, or to a particular group.',
  },
  ao: {
    term: 'AO — Officer of the Order of Australia',
    def: 'The second-highest level, for distinguished service of a high degree to Australia or to humanity at large.',
  },
  ac: {
    term: 'AC — Companion of the Order of Australia',
    def: 'The highest level of the Order, for eminent achievement and merit of the highest degree. Fewer than 700 have ever been awarded.',
  },
  order: {
    term: 'Order of Australia',
    def: 'Australia’s own honours system, created in 1975 to replace British Imperial honours. It has a General Division (for civilians, nominated by the public) and a Military Division.',
  },
  imperial: {
    term: 'Imperial honours',
    def: 'British honours — knighthoods, the Order of the British Empire (OBE, MBE, CBE), and similar — awarded in Australia until the system was wound back. They ran alongside the Order of Australia for years after 1975: the Order only overtook them in 1983, and routine Imperial gazettals continued until 1989 before collapsing to a handful in 1990–92. A few later records remain, mostly the Royal Victorian Order, which is in the Sovereign’s personal gift and was never the Australian government’s to withdraw.',
  },
  realm: {
    term: 'Realm awards',
    def: 'Long-service and campaign medals awarded across the Commonwealth realms, including the National Medal. These make up the largest share of the register by a wide margin.',
  },
  'national-medal': {
    term: 'National Medal',
    def: 'A long-service medal for 15 years of diligent service in the police, fire, ambulance, emergency and corrective services, and some volunteer organisations. It is the single most common entry in the register — about two in five records — and it recognises length of service rather than a particular achievement.',
  },
  meritorious: {
    term: 'Meritorious awards',
    def: 'Awards such as the Public Service Medal, the Australian Police Medal and the Ambulance Service Medal, given for distinguished service within a particular profession.',
  },
  bravery: {
    term: 'Bravery decorations',
    def: 'Awards including the Cross of Valour, the Star of Courage, the Bravery Medal and the Commendation for Brave Conduct, for acts of bravery in hazardous circumstances.',
  },
  citation: {
    term: 'Citation',
    def: 'The short published statement of what a person was recognised for — usually of the form “For service to …”. It is the only description the register publishes.',
  },
  gazette: {
    term: 'Gazetted',
    def: 'Formally published in the Commonwealth of Australia Gazette, the official record of government notices. An honour becomes public when it is gazetted.',
  },
  'honours-list': {
    term: 'Honours list',
    def: 'The twice-yearly batches in which most Order of Australia awards are announced: the Australia Day list (26 January) and the King’s Birthday list (June, previously the Queen’s Birthday).',
  },
  division: {
    term: 'General and Military divisions',
    def: 'The Order of Australia splits into a General Division for civilians and a Military Division for members of the Australian Defence Force. Honorary awards to non-citizens are recorded separately.',
  },
  'post-nominal': {
    term: 'Post-nominal',
    def: 'The letters a recipient may place after their name — AC, AO, AM, OAM, and so on.',
  },
  'per-capita': {
    term: 'Per 10,000 residents',
    def: 'Awards divided by the postcode’s usual resident population at the 2021 Census, multiplied by 10,000. Raw counts mostly measure how many people live somewhere, so a rate is the only fair comparison.',
  },
  'age-controlled': {
    term: 'Per 10,000 residents aged 65+',
    def: 'The same awards divided by residents aged 65 and over instead of everyone. Order of Australia recipients skew elderly, so this checks whether a postcode’s high rate is really just an older population.',
  },
  decile: {
    term: 'Income decile',
    def: 'Postcodes sorted by median weekly household income at the 2021 Census and split into ten equal-sized groups. Decile 1 is the lowest-income tenth, decile 10 the highest.',
  },
  honorific: {
    term: 'Honorific',
    def: 'The title a person is gazetted under — Mr, Mrs, Ms, Miss, Dr, Professor, or a military rank. The register publishes no sex field, so titles are the only signal available, and they only work for the titles that carry sex.',
  },
  'service-domain': {
    term: 'Service domain',
    def: 'A grouping of citations by subject — health, education, sport, veterans and so on — derived here by reading the citation text. It is an editorial classification made for this site, not something the register publishes.',
  },
  'honorary-award': {
    term: 'Honorary award',
    def: 'An award to a person who is not an Australian citizen. Honorary recipients do not use the post-nominal letters.',
  },
};

let pop: HTMLElement | null = null;

function hide(): void {
  pop?.remove();
  pop = null;
}

function show(anchor: HTMLElement, key: string): void {
  hide();
  const entry = GLOSSARY[key];
  if (!entry) return;
  pop = document.createElement('div');
  pop.className = 'gloss-pop';
  pop.setAttribute('role', 'dialog');
  pop.setAttribute('aria-label', entry.term);
  pop.innerHTML = `<dl><dt>${escapeHtml(entry.term)}</dt><dd>${escapeHtml(entry.def)}</dd></dl>`;
  document.body.appendChild(pop);

  const r = anchor.getBoundingClientRect();
  const pr = pop.getBoundingClientRect();
  let left = r.left;
  let top = r.bottom + 8;
  if (left + pr.width > window.innerWidth - 12) left = window.innerWidth - pr.width - 12;
  if (top + pr.height > window.innerHeight - 12) top = r.top - pr.height - 8;
  pop.style.left = `${Math.max(12, left)}px`;
  pop.style.top = `${Math.max(12, top)}px`;
}

/**
 * One delegated listener for the whole document. Like every overlay here the
 * popover DETACHES rather than hiding, and it closes on outside pointerdown and
 * on Escape as well as on re-click.
 */
export function initGlossary(): void {
  document.addEventListener('click', (e) => {
    const link = (e.target as Element).closest<HTMLElement>('.glossary-link');
    if (link) {
      const key = link.dataset.term ?? '';
      const wasOpen = pop !== null && pop.getAttribute('aria-label') === GLOSSARY[key]?.term;
      if (wasOpen) hide();
      else show(link, key);
      e.stopPropagation();
      return;
    }
    if (pop && !pop.contains(e.target as Node)) hide();
  });
  document.addEventListener('pointerdown', (e) => {
    if (pop && !pop.contains(e.target as Node) && !(e.target as Element).closest('.glossary-link')) {
      hide();
    }
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') hide();
  });
  window.addEventListener('resize', hide);
}

/** Inline glossary trigger for use inside an HTML string. */
export function gloss(key: string, label?: string): string {
  const entry = GLOSSARY[key];
  const text = label ?? entry?.term ?? key;
  return `<button type="button" class="glossary-link" data-term="${escapeHtml(key)}" aria-label="What is ${escapeHtml(text)}?">${escapeHtml(text)}</button>`;
}
